/* eslint-disable @typescript-eslint/no-explicit-any */
import { useCallback, useState } from 'react';
import { withAuth } from '../../context/Auth';
import withContainer from '../../components/Container/withContainer';
import { AutoFlexButton, TextMBig } from '../../ui/Styled/Styled';
import { Form } from 'ui/FormContainer/FormContainer';

import FormField from '../../ui/FormField/FormField';
import { AssignCourseFormData } from '../../api/courses/courses.types';

import { assignCourse, getCoursesOptions } from '../../api/courses/courses';
import Button from '../../ui/Button/Button';
import { SelectField, OptionProps } from '../../ui/Select';
import { makeCreateFormFunc } from 'utils/api';
import { useCurrentOrganizationRequired } from 'context/User';
import { isoDateWithoutTime } from 'utils/date';
import { generateMoodleReq, getMoodleCreds } from 'api/moodle/moodle';
import InputInformation from 'ui/Input/InputInformation';
import { MoodleRequisites } from 'api/moodle/moodle.types';
import ButtonsContainer from 'ui/Button/ButtonContainer';
import { getStudentsOptions } from 'api/students/students';
import { EndDateField } from './components/fields';
import { AssignOrganizationField } from './components/AssignOrganizationField';
import ButtonsContainerRow from 'ui/Button/ButtonContainerRow';
import { FormRow } from 'ui/Form/Form.styled';
import { RouterPaths } from 'helpers/router-paths';
import { OrganizationFullInfo } from 'api/organizations/organizations.types';
import { OrganizationType } from 'api/api';
import { QueryKey } from 'helpers/constants';
import useUpdateEffect from 'hooks/useUpdateEffect';
import { EducationCenterField } from 'components/fields/EducationCenterField';

const Field = FormField<AssignCourseFormData>;
const Select = SelectField<AssignCourseFormData>;

const handleFormSubmit = makeCreateFormFunc(assignCourse, 'Курс назначен!');

const initialValues: Partial<AssignCourseFormData> = {
    dateStartLearning: isoDateWithoutTime(new Date().toISOString())
};

const AssignCoursePage = () => {
    const currentOrganization = useCurrentOrganizationRequired();
    const [selectedOrganization, setSelectedOrganization] =
        useState<OrganizationFullInfo>(currentOrganization);
    const [isGenerationActive, setIsGenerationActive] = useState(false);
    const [selectedStudent, setSelectedStudent] = useState<string>();
    const [reqDataMoodleUser, setReqDataMoodleUser] =
        useState<MoodleRequisites | null>();

    const generationReqMoodle = useCallback(async () => {
        setIsGenerationActive(false);
        if (selectedStudent) {
            setReqDataMoodleUser(await generateMoodleReq(selectedStudent));
        }
    }, [selectedStudent]);

    useUpdateEffect(() => {
        setSelectedOrganization(currentOrganization);
    }, [currentOrganization]);

    const onStudentChange = useCallback((value: string) => {
        setReqDataMoodleUser(null);
        setSelectedStudent(value);

        getMoodleCreds(value)
            .then((reqData) => {
                if (reqData.login) {
                    setReqDataMoodleUser(reqData);
                } else {
                    setIsGenerationActive(true);
                }
            })
            .catch(() => {
                setIsGenerationActive(true);
            });
    }, []);

    const onGetStudents = useCallback(
        (search?: string) =>
            getStudentsOptions(search, selectedOrganization.id),
        [selectedOrganization]
    );

    const onGetCourses = useCallback(
        (search?: string) =>
            getCoursesOptions(
                search,
                selectedOrganization.organizationType ===
                    OrganizationType.EducationCenter
                    ? selectedOrganization.id
                    : undefined
            ),
        [selectedOrganization]
    );

    const onOrganizationSelect = useCallback(
        (_value: number, option?: OptionProps<number>) => {
            setSelectedOrganization(option?.data);
            setIsGenerationActive(false);
        },
        []
    );

    const orgIdKey =
        QueryKey.Organization + ':' + selectedOrganization.id.toString();

    return (
        <Form<AssignCourseFormData>
            initialValues={initialValues}
            onSubmit={handleFormSubmit}
        >
            {({ submitting, form }) => (
                <>
                    <FormRow>
                        <AssignOrganizationField
                            form={form}
                            value={selectedOrganization.id}
                            onChange={onOrganizationSelect}
                        />
                        <AutoFlexButton href={RouterPaths.CreateOrganization}>
                            Добавить
                        </AutoFlexButton>
                    </FormRow>

                    <FormRow>
                        <Select
                            name="studentId"
                            onSearch={onGetStudents}
                            onChange={onStudentChange}
                            label="Сотрудник"
                            required
                            placeholder="Выберите сотрудника"
                            queryKey={[orgIdKey, QueryKey.Student]}
                        />
                        <AutoFlexButton href={RouterPaths.CreateStudents}>
                            Создать
                        </AutoFlexButton>
                    </FormRow>
                    <FormRow>
                        <Select
                            name="courseId"
                            onSearch={onGetCourses}
                            label="Курс"
                            required
                            queryKey={[orgIdKey, QueryKey.Course]}
                        />
                        <AutoFlexButton href={RouterPaths.CreateCourse}>
                            Добавить
                        </AutoFlexButton>
                    </FormRow>

                    <Field
                        name="dateStartLearning"
                        label="Дата начала обучения"
                        type="date"
                        required
                    />

                    <EndDateField />
                    <EducationCenterField />

                    <ButtonsContainerRow>
                        <TextMBig>Данные для Moodle:</TextMBig>
                        {!reqDataMoodleUser?.login && (
                            <Button
                                disabled={!isGenerationActive}
                                onClick={generationReqMoodle}
                            >
                                Сгенерировать
                            </Button>
                        )}
                    </ButtonsContainerRow>

                    <InputInformation
                        label="Логин"
                        value={reqDataMoodleUser?.login}
                    />
                    <InputInformation
                        label="Пароль"
                        value={reqDataMoodleUser?.password}
                    />
                    <ButtonsContainer>
                        <Button type="submit" disabled={submitting}>
                            Назначить
                        </Button>
                    </ButtonsContainer>
                </>
            )}
        </Form>
    );
};

export default withAuth(
    withContainer(AssignCoursePage, {
        heading: 'Назначить курс'
    })
);
